"use client";

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { X } from "lucide-react";
import { prophetsApi } from "@/lib/api/prophets";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { ProphetChat } from "@/components/prophets/ProphetChat";
import { ProphetImagePlaceholder } from "@/components/prophets/ProphetImagePlaceholder";
import { Button } from "@/components/ui/button";
import { useProphetWidgetStore } from "@/lib/stores/prophetWidgetStore";

export function FloatingProphetWidget() {
  const t = useTranslations("Prophet");
  const { isOpen: open, initialPrompt, setOpen, clearInitialPrompt } = useProphetWidgetStore();

  const { data: user } = useCurrentUser();
  const selectedProphetId = user?.settings?.selected_prophet_id;

  const { data: prophets } = useQuery({
    queryKey: ["prophets"],
    queryFn: prophetsApi.listProphets,
    staleTime: 60_000,
    enabled: !!selectedProphetId,
  });

  const prophet = prophets?.find((p) => p.id === selectedProphetId);

  if (!prophet) return null;

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-50 sm:inset-auto sm:bottom-24 sm:right-6 sm:h-[42rem] sm:w-[28rem]">
          <ProphetChat
            prophet={prophet}
            initialPrompt={initialPrompt}
            onInitialPromptHandled={clearInitialPrompt}
            className="h-full rounded-none border-0 shadow-none sm:rounded-lg sm:border sm:shadow-xl"
            headerRight={
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setOpen(false)}
                aria-label={t("closeChat")}
              >
                <X className="h-4 w-4" />
              </Button>
            }
          />
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label={open ? t("closeChat") : t("openChat", { name: prophet.name })}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border border-border bg-surface shadow-xl transition-transform hover:scale-105"
      >
        {open ? (
          <X className="h-6 w-6 text-text" />
        ) : prophet.image_url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={prophet.image_url} alt={prophet.name} className="h-full w-full object-cover" />
        ) : (
          <ProphetImagePlaceholder className="h-full w-full rounded-full" />
        )}
      </button>
    </>
  );
}
